/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --animation-duration: var(--nve-ref-animation-duration-200);
  margin: calc(var(--nve-sys-layer-popover-offset) + calc(var(--nve-sys-layer-popover-arrow-offset) * 2));
  border: 0;
  padding: 0;
  background: none;
  position: fixed;
  overflow: visible;
  contain: initial;
  width: max-content;
  z-index: 99;
  inset: revert;
  interest-delay-start: 0.5s;
}

:host([popover]) {
  opacity: 0;
  pointer-events: none;
}

/* apply transitions during beforetoggle event to prevent flash of unstyled popover on page load */
:host(:state(transition-start)) {
  transition-duration: var(--animation-duration);
  transition-timing-function: ease-in-out;
  transition-property: opacity, display, overlay, translate, transform;
  transition-behavior: allow-discrete;
}

/** workaround for Safari / Firefox which do not respect transitions on implicit anchor positioning */
@supports not (container-type: anchored) {
  :host {
    --animation-duration: 0;
  }
}

:host(:popover-open) {
  opacity: 1;
  pointer-events: initial;

  @starting-style {
    opacity: 0;
  }
}

/* lit SSR ensure popovers are not visible until hydration has happened */
:host(:not([nve-popover])),
:host([defer-hydration]) {
  opacity: 0 !important;
  pointer-events: none !important;
}

[internal-host] {
  width: var(--width);
  min-width: var(--min-width);
  font-family: inherit;
  display: flex;
  flex-direction: column;
  overflow: visible;
  white-space: nowrap;
  z-index: 9999;
  border: 0;
  margin: 0;
  user-select: text;
}

@supports (position-area: bottom) {
  [internal-host] {
    position: relative;
  }

  .arrow {
    position-area: bottom;
    translate: 0% 50%;
  }

  :host([position='top']) .arrow {
    position-area: bottom;
    translate: 0% 50%;
  }

  :host([position='top'][alignment='end']) .arrow {
    position-area: bottom right;
    translate: -50% 50%;
  }

  :host([position='top'][alignment='start']) .arrow {
    position-area: bottom left;
    translate: 50% 50%;
  }

  :host([position='bottom']) .arrow {
    position-area: top;
    translate: 0% -50%;
  }

  :host([position='bottom'][alignment='end']) .arrow {
    position-area: top right;
    translate: -50% -50%;
  }

  :host([position='bottom'][alignment='start']) .arrow {
    position-area: top left;
    translate: 50% -50%;
  }

  :host([position='left']) .arrow {
    position-area: right;
    translate: 50% 0%;
  }

  :host([position='left'][alignment='end']) .arrow {
    position-area: bottom right;
    translate: 50% -50%;
  }

  :host([position='left'][alignment='start']) .arrow {
    position-area: top right;
    translate: 50% 50%;
  }

  :host([position='right']) .arrow {
    position-area: left;
    translate: -50% 0%;
  }

  :host([position='right'][alignment='end']) .arrow {
    position-area: bottom left;
    translate: -50% -50%;
  }

  :host([position='right'][alignment='start']) .arrow {
    position-area: top left;
    translate: -50% 50%;
  }

  /** positioning */
  :host([position='center']) {
    position-area: center;
  }

  :host {
    inset: auto !important;
    place-self: normal !important;

    /*
     * This line is critical for shadow root positioning and auto anchor association
     * https://developer.mozilla.org/en-US/docs/Web/CSS/Guides/Anchor_positioning/Using#implicit_anchor_association
     */
    position-anchor: auto !important;
  }

  /** top */
  :host([position='top']) {
    position-area: top center;
  }

  :host([position='top'][alignment='start']) {
    position-area: top left;
  }

  :host([position='top'][alignment='end']) {
    position-area: top right;
  }

  :host([position='top'][alignment='start']) {
    position-area: top span-right;
  }

  :host([position='top'][alignment='end']) {
    position-area: top span-left;
  }

  /** bottom */
  :host([position='bottom']) {
    position-area: bottom center;
  }

  :host([position='bottom'][alignment='start']) {
    position-area: bottom span-right;
  }

  :host([position='bottom'][alignment='end']) {
    position-area: bottom span-left;
  }

  /** left */
  :host([position='left']) {
    position-area: left center;
  }

  :host([position='left'][alignment='end']) {
    position-area: left span-top;
  }

  :host([position='left'][alignment='start']) {
    position-area: left span-bottom;
  }

  /** right */
  :host([position='right']) {
    position-area: right center;
  }

  :host([position='right'][alignment='end']) {
    position-area: right span-top;
  }

  :host([position='right'][alignment='start']) {
    position-area: right span-bottom;
  }

  :host([position='top']) {
    position-try-fallbacks: top, bottom, left, right;
  }

  :host([position='bottom']) {
    position-try-fallbacks: bottom, top, left, right;
  }

  :host([position='left']) {
    position-try-fallbacks: left, right, top, bottom;
  }

  :host([position='right']) {
    position-try-fallbacks: right, left, top, bottom;
  }
}

:host([position='center'][alignment='center']) {
  position-area: unset !important;
  margin: auto !important;
  inset: 0 !important;
}

/*
 * Remove the following @supports block after Chrome 145 is stable.
 * This is to compensate for a change in Chrome 144 for how anchor positioning with transforms works.
 * https://developer.chrome.com/blog/chrome-144-beta#css_anchor_positioning_with_transforms
 * https://x.com/una/status/1995538970223018004
 * 
 * The (not selector(::search-text)) selector is used to target Chrome 143 and below.
 */
@supports (not selector(::search-text)) and (position-area: bottom) {
  .arrow {
    translate: 0% -50%;
  }

  :host([position='top']) .arrow {
    translate: 0% -50%;
  }

  :host([position='top'][alignment='end']) .arrow {
    translate: -150% -50%;
  }

  :host([position='top'][alignment='start']) .arrow {
    translate: 150% -50%;
  }

  :host([position='bottom']) .arrow {
    translate: 0% 50%;
  }

  :host([position='bottom'][alignment='end']) .arrow {
    translate: -150% 50%;
  }

  :host([position='bottom'][alignment='start']) .arrow {
    translate: 150% 50%;
  }

  :host([position='left']) .arrow {
    translate: -50% 0%;
  }

  :host([position='left'][alignment='end']) .arrow {
    translate: -50% -150%;
  }

  :host([position='left'][alignment='start']) .arrow {
    translate: -50% 150%;
  }

  :host([position='right']) .arrow {
    translate: 50% 0%;
  }

  :host([position='right'][alignment='end']) .arrow {
    translate: 50% -150%;
  }

  :host([position='right'][alignment='start']) .arrow {
    translate: 50% 150%;
  }
}

:host([popover][modal])::before {
  content: ' ';
  display: block;
  inset: 0;
  z-index: 99;
  position: fixed;
}
